/* eslint-disable local/no-new-type-assertions */

import { useSubscription } from '@apollo/client'
import { Box, Flex, HStack, SkeletonText, Text } from '@chakra-ui/react'
import { useCallback, useState } from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'

import { useUser } from '@/client/context/user'
import RPC from '@/client/rpc'
import type {
  MessageEmbedV1AssessmentAssessmentMessageSubscription,
  MessageEmbedV1AssessmentAssessmentMessageSubscriptionVariables,
  MessageEmbedV1MessageFragment,
} from '@/client/services/hasura/generated/graphql'

import Form, { FormDefaultFooter } from '@/components/UI/Form/Form'
import FormFooterEmpty from '@/components/UI/Form/FormFooterEmpty'
import type { FormBlocks, FormValues } from '@/components/UI/Form/types'

import { SUBSCRIPTION_MESSAGE_EMBED_V1_ASSESSMENT_ASSESSMENT_MESSAGE } from './MessageEmbedV1Assessment.hasura'
import MessageEmbedV1AssessmentInputRadio from './MessageEmbedV1AssessmentInputRadio'

function transformEmbedAssessmentFormBlocks(
  formBlocks: FormBlocks,
  values: FormValues | null
): FormBlocks {
  return formBlocks.map(item => ({
    ...item,
    Component: item.type === 'radio' ? MessageEmbedV1AssessmentInputRadio : undefined,
    initialValue: values?.[item.name] ?? null,
  })) as FormBlocks
}

function MessageEmbedV1AssessmentForm({
  assessment,
}: {
  assessment: MessageEmbedV1AssessmentAssessmentMessageSubscription['assessment'][number]
}) {
  const user = useUser()
  const [selfSubmitted, setSelfSubmitted] = useState(false)

  const selfSubmission = assessment.assessment_submissions.find(item => item.user?.id === user.id)
  const disabled = selfSubmission != null || selfSubmitted

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      await RPC.submitAssessment({ assessmentId: assessment.id, seriesId: null, responses: values })
      setSelfSubmitted(true)
    },
    [assessment.id]
  )

  return (
    <Box width="100%">
      <Form
        name="AssessmentBuilderPreview"
        direction="vertical"
        blocks={transformEmbedAssessmentFormBlocks(
          assessment.form_blocks,
          selfSubmission?.responses
        )}
        disabled={disabled}
        onSubmit={handleSubmit}
        FooterComponent={disabled ? FormFooterEmpty : FormDefaultFooter}
      />
    </Box>
  )
}

export default function MessageEmbedV1Assessment({
  message,
}: {
  message: MessageEmbedV1MessageFragment
}): React.ReactElement {
  const assessmentId = message.body_safe?.id ?? 0

  const { loading, error, data } = useSubscription<
    MessageEmbedV1AssessmentAssessmentMessageSubscription,
    MessageEmbedV1AssessmentAssessmentMessageSubscriptionVariables
  >(SUBSCRIPTION_MESSAGE_EMBED_V1_ASSESSMENT_ASSESSMENT_MESSAGE, {
    variables: {
      id: assessmentId,
    },
  })

  let content = null
  const assessment = data?.assessment?.[0]

  if (loading) {
    content = (
      <HStack width={{ base: '250px', lg: '500px' }}>
        <SkeletonText noOfLines={6} width="100%" />
      </HStack>
    )
  } else if (error != null || assessment == null) {
    content = <Text color="fg.error">[Error loading assessment]</Text>
  } else {
    content = (
      <Box width="100%">
        <Text fontWeight="bold">{assessment.title}</Text>
        <MessageEmbedV1AssessmentForm assessment={assessment} />
      </Box>
    )
  }

  return (
    <Flex className="embed-root" justifyContent="center" margin="16px">
      <Box
        border={`1px solid ${chakraTokenToVar('border')}`}
        borderRadius="16px"
        padding="24px"
        width={{ base: '100%', md: '686px' }}
      >
        <Flex align="flex-start" direction="column">
          {content}
        </Flex>
      </Box>
    </Flex>
  )
}
